// The VOIDLINE roster. Eight craft, eight manufacturers, no duplicates.
// Every number here feeds directly into the physics step — these are not cosmetic.

export interface VehicleStats {
  /** Base top speed, m/s (drag-limited). */
  topSpeed: number;
  /** Engine acceleration, m/s^2. */
  acceleration: number;
  /** Max yaw rate at low speed, rad/s. */
  turnRate: number;
  /** How quickly actual yaw rate follows the stick (1/s). Low = heavy, high = darty. */
  steerResponse: number;
  /** Lateral grip: how fast sideways velocity is bled (1/s). */
  grip: number;
  /** Grip fraction while airbraking/drifting (lower = looser drift). */
  driftGrip: number;
  /** Extra acceleration while boosting, m/s^2. */
  boostPower: number;
  /** Boost tank capacity in seconds of continuous burn. */
  boostTank: number;
  /** Collision mass (push authority in ship-to-ship contact). */
  mass: number;
  /** Pitch/steer authority while airborne (0..1.5). */
  airControl: number;
  /** Fraction of speed retained in wall/ship impacts (0..1). */
  toughness: number;
}

export interface PaintScheme {
  id: string;
  name: string;
  /** Hull base color. */
  primary: string;
  /** Panel/secondary color. */
  secondary: string;
  /** Emissive accent (engines, strips). */
  glow: string;
  /** Player level required (0 = always available). */
  unlockLevel: number;
}

export interface VehicleDef {
  id: string;
  name: string;
  manufacturer: string;
  archetype: string;
  description: string;
  /** Ship silhouette family used by the mesh generator. */
  hull: 'dart' | 'wing' | 'fork' | 'blade' | 'hammer' | 'ray' | 'quill' | 'prism';
  stats: VehicleStats;
  paints: PaintScheme[];
  unlockLevel: number;
}

const p = (
  id: string,
  name: string,
  primary: string,
  secondary: string,
  glow: string,
  unlockLevel = 0
): PaintScheme => ({ id, name, primary, secondary, glow, unlockLevel });

export const VEHICLES: VehicleDef[] = [
  {
    id: 'kestrel',
    name: 'Kestrel VX',
    manufacturer: 'Halcyon Dynamics',
    archetype: 'All-rounder',
    description:
      'The academy standard. Forgiving grip, honest speed, no vices. Every VOIDLINE champion flew one once.',
    hull: 'dart',
    unlockLevel: 0,
    stats: {
      topSpeed: 94,
      acceleration: 30,
      turnRate: 1.55,
      steerResponse: 7,
      grip: 5.2,
      driftGrip: 0.42,
      boostPower: 26,
      boostTank: 2.6,
      mass: 1.0,
      airControl: 0.9,
      toughness: 0.82,
    },
    paints: [
      p('academy', 'Academy White', '#dfe6ee', '#2b3a4d', '#31c8ff'),
      p('signal', 'Signal Orange', '#e8641f', '#1d2530', '#ffb347', 3),
      p('nightline', 'Nightline', '#151a24', '#3d4c63', '#7a5cff', 7),
      p('champion', 'Champion Gold', '#c9a227', '#20242e', '#ffe08a', 14),
    ],
  },
  {
    id: 'rampart',
    name: 'Rampart',
    manufacturer: 'Bulwark Foundry',
    archetype: 'Heavy tank',
    description:
      'Four hundred tons of magnetized ferroceramic. Slow off the line, nearly impossible to shove aside, and murderous on long straights.',
    hull: 'hammer',
    unlockLevel: 0,
    stats: {
      topSpeed: 99,
      acceleration: 22,
      turnRate: 1.12,
      steerResponse: 4.2,
      grip: 6.0,
      driftGrip: 0.55,
      boostPower: 22,
      boostTank: 2.2,
      mass: 1.85,
      airControl: 0.55,
      toughness: 0.95,
    },
    paints: [
      p('foundry', 'Foundry Grey', '#8d9298', '#3a3f45', '#ffa028'),
      p('hazard', 'Hazard Stripe', '#d8b021', '#23262b', '#ffd025', 4),
      p('siege', 'Siege Green', '#3c4a38', '#181c17', '#9fff5e', 9),
      p('meteor', 'Meteor Red', '#6e2020', '#2a2222', '#ff5a3c', 16),
    ],
  },
  {
    id: 'nix7',
    name: 'NIX-7',
    manufacturer: 'Nixie Orbital',
    archetype: 'Sprinter',
    description:
      'A racing drone that grew a cockpit. Snaps to full throttle instantly — its ceiling is lower, but nobody beats a NIX out of a hairpin.',
    hull: 'quill',
    unlockLevel: 0,
    stats: {
      topSpeed: 88,
      acceleration: 40,
      turnRate: 1.7,
      steerResponse: 9,
      grip: 5.6,
      driftGrip: 0.4,
      boostPower: 24,
      boostTank: 2.4,
      mass: 0.75,
      airControl: 1.05,
      toughness: 0.72,
    },
    paints: [
      p('drone', 'Drone Cyan', '#9fd8d4', '#1e3231', '#3cffd9'),
      p('wasp', 'Wasp', '#cbb32a', '#191a10', '#f4ff5e', 3),
      p('ion', 'Ion Violet', '#4f3a8c', '#191426', '#b98aff', 8),
      p('zerog', 'Zero-G White', '#f2f4f8', '#a5aebc', '#7ee7ff', 12),
    ],
  },
  {
    id: 'sableline',
    name: 'Sableline',
    manufacturer: 'Kuroda Velocity Works',
    archetype: 'Drift specialist',
    description:
      'Kuroda built the Sableline around one idea: the corner is the overtake. Loose tail, feathered airbrakes, and a boost meter that loves sideways.',
    hull: 'blade',
    unlockLevel: 2,
    stats: {
      topSpeed: 93,
      acceleration: 31,
      turnRate: 1.62,
      steerResponse: 7.5,
      grip: 4.4,
      driftGrip: 0.24,
      boostPower: 27,
      boostTank: 2.8,
      mass: 0.95,
      airControl: 0.95,
      toughness: 0.78,
    },
    paints: [
      p('ink', 'Sumi Ink', '#16161c', '#37373f', '#ff2d6f'),
      p('vermilion', 'Vermilion', '#8c1f1f', '#1c1416', '#ff6a3c', 5),
      p('ghost', 'Ghost Pearl', '#c9ccd6', '#5a5e6a', '#ff9ecf', 10),
      p('daybreak', 'Daybreak', '#d97b29', '#27180e', '#ffd23c', 15),
    ],
  },
  {
    id: 'aurelion',
    name: 'Aurelion GT',
    manufacturer: 'Aurel Institute',
    archetype: 'Precision',
    description:
      'Institute engineers call the steering column "the scalpel". Rails through technical sections; punishes nobody. The lap time is your fault.',
    hull: 'wing',
    unlockLevel: 3,
    stats: {
      topSpeed: 92,
      acceleration: 32,
      turnRate: 1.78,
      steerResponse: 10,
      grip: 6.4,
      driftGrip: 0.5,
      boostPower: 24,
      boostTank: 2.4,
      mass: 1.0,
      airControl: 1.0,
      toughness: 0.8,
    },
    paints: [
      p('institute', 'Institute Blue', '#2b4d7e', '#141c28', '#4db8ff'),
      p('laurel', 'Laurel', '#3f6448', '#131a15', '#8affc1', 5),
      p('argent', 'Argent', '#b9bfc9', '#454b56', '#dff3ff', 11),
      p('regalia', 'Regalia', '#4a2a66', '#171021', '#c580ff', 17),
    ],
  },
  {
    id: 'pyrefalcon',
    name: 'Pyre Falcon',
    manufacturer: 'Emberworks Collective',
    archetype: 'Boost engine',
    description:
      'Half the hull is combustion chamber. The Falcon carries the biggest boost reserve on the grid and spends it like a gambler on a heater.',
    hull: 'fork',
    unlockLevel: 5,
    stats: {
      topSpeed: 91,
      acceleration: 29,
      turnRate: 1.5,
      steerResponse: 6.5,
      grip: 5.0,
      driftGrip: 0.4,
      boostPower: 34,
      boostTank: 3.6,
      mass: 1.1,
      airControl: 0.85,
      toughness: 0.8,
    },
    paints: [
      p('ember', 'Ember', '#93331b', '#221410', '#ff7a1a'),
      p('coal', 'Coalback', '#1d1b1a', '#403734', '#ff4d00', 7),
      p('solar', 'Solar Flare', '#c2751d', '#2b1c0d', '#ffd75e', 12),
      p('afterglow', 'Afterglow', '#5e1f4d', '#1c0f1a', '#ff5ad0', 18),
    ],
  },
  {
    id: 'glacio',
    name: 'Glacio S/A',
    manufacturer: 'Borealis Yards',
    archetype: 'Sky dancer',
    description:
      'Built for the aurora circuits: enormous air control and a hover cradle that lands like snowfall. In the air, nothing touches it.',
    hull: 'ray',
    unlockLevel: 7,
    stats: {
      topSpeed: 95,
      acceleration: 28,
      turnRate: 1.45,
      steerResponse: 6.8,
      grip: 5.4,
      driftGrip: 0.45,
      boostPower: 23,
      boostTank: 2.3,
      mass: 1.05,
      airControl: 1.45,
      toughness: 0.85,
    },
    paints: [
      p('boreal', 'Boreal Teal', '#1f5d5b', '#0e1d1f', '#5effe0'),
      p('midnightsun', 'Midnight Sun', '#2a2d4d', '#101224', '#8a9bff', 9),
      p('driftice', 'Drift Ice', '#dbe9f2', '#7f97a8', '#aef4ff', 13),
      p('polarfire', 'Polar Fire', '#7e3a5a', '#1e1018', '#ff7ab8', 19),
    ],
  },
  {
    id: 'vantablade',
    name: 'Vantablade',
    manufacturer: 'Meridian Null',
    archetype: 'Expert weapon',
    description:
      'Meridian Null does not publish specifications. Fastest thing on the grid, boost like a railgun — and it will kill your race the moment you stop respecting it.',
    hull: 'prism',
    unlockLevel: 12,
    stats: {
      topSpeed: 105,
      acceleration: 33,
      turnRate: 1.85,
      steerResponse: 12,
      grip: 4.0,
      driftGrip: 0.22,
      boostPower: 36,
      boostTank: 2.6,
      mass: 0.85,
      airControl: 1.1,
      toughness: 0.6,
    },
    paints: [
      p('null', 'Null Black', '#0b0b10', '#1e1e2a', '#c8f34c'),
      p('redshift', 'Redshift', '#1a0d12', '#3a1220', '#ff2244', 14),
      p('spectral', 'Spectral', '#12131c', '#2c3141', '#66f7ff', 17),
      p('singularity', 'Singularity', '#07070a', '#131019', '#b366ff', 22),
    ],
  },
];

export const VEHICLE_IDS = VEHICLES.map((v) => v.id);

export function getVehicle(id: string): VehicleDef {
  return VEHICLES.find((v) => v.id === id) ?? VEHICLES[0];
}

export function getPaint(vehicle: VehicleDef, paintId: string): PaintScheme {
  return vehicle.paints.find((s) => s.id === paintId) ?? vehicle.paints[0];
}

/** Normalized 0..1 display bars for the garage UI. */
export function displayStats(v: VehicleDef): { label: string; value: number }[] {
  const s = v.stats;
  return [
    { label: 'Top Speed', value: norm(s.topSpeed, 84, 106) },
    { label: 'Thrust', value: norm(s.acceleration, 20, 42) },
    { label: 'Handling', value: norm(s.turnRate * s.steerResponse, 4, 23) },
    { label: 'Grip', value: norm(s.grip, 3.8, 6.6) },
    { label: 'Boost', value: norm(s.boostPower * s.boostTank, 45, 125) },
    { label: 'Armor', value: norm(s.toughness * s.mass, 0.4, 1.8) },
  ];
}

function norm(v: number, min: number, max: number): number {
  return Math.max(0.05, Math.min(1, (v - min) / (max - min)));
}
